import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { apiService } from "../../services/apiService";

export const fetchVehicleConstructionTypesDropdown = createAsyncThunk(
  "vehicleDropdowns/fetchVehicleConstructionTypesDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getVehicleConstructionTypesDropdown();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch vehicle construction types");
    }
  }
);

export const fetchVehicleTypesDropdown = createAsyncThunk(
  "vehicleDropdowns/fetchVehicleTypesDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getVehicleTypesDropdown();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch vehicle types");
    }
  }
);

export const fetchVehicleTransmissionTypesDropdown = createAsyncThunk(
  "vehicleDropdowns/fetchVehicleTransmissionTypesDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getVehicleTransmissionTypesDropdown();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch vehicle transmission types");
    }
  }
);

export const fetchVehicleMakesDropdown = createAsyncThunk(
  "vehicleDropdowns/fetchVehicleMakesDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getVehicleMakesDropdown();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch vehicle makes");
    }
  }
);

export const fetchVehicleModelsDropdown = createAsyncThunk(
  "vehicleDropdowns/fetchVehicleModelsDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getVehicleModelsDropdown();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch vehicle models");
    }
  }
);

export const fetchVehicleCompanyBranchesDropdown = createAsyncThunk(
  "vehicleDropdowns/fetchVehicleCompanyBranchesDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getCompanyBranchesDropdown();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch company branches");
    }
  }
);

export const fetchVehicleVendorsDropdown = createAsyncThunk(
  "vehicleDropdowns/fetchVehicleVendorsDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getVehicleVendorsDropdown();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch vehicle vendors");
    }
  }
);

export const fetchVehicleThirdPartyOwnersDropdown = createAsyncThunk(
  "vehicleDropdowns/fetchVehicleThirdPartyOwnersDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getVehicleThirdPartyOwnersDropdown();
      return response;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch vehicle third-party owners");
    }
  }
);

const extractPayloadData = (payload, fallback = []) =>
  payload?.data !== undefined ? payload.data : payload || fallback;

const initialState = {
  vehicleTypesDropdownData: [],
  vehicleConstructionTypesDropdownData: [],
  vehicleTransmissionTypesDropdownData: [],
  vehicleMakesDropdownData: [],
  vehicleModelsDropdownData: [],
  vehicleCompanyBranchesDropdownData: [],
  vehicleVendorsDropdownData: [],
  vehicleThirdPartyOwnersDropdownData: [],
  vehicleDropdownLoadingCount: 0,
  vehicleDropdownError: null,
};

const vehicleDropdownSlice = createSlice({
  name: "vehicleDropdowns",
  initialState,
  reducers: {
    clearVehicleDropdownState: (state) => {
      state.vehicleTypesDropdownData = [];
      state.vehicleConstructionTypesDropdownData = [];
      state.vehicleTransmissionTypesDropdownData = [];
      state.vehicleMakesDropdownData = [];
      state.vehicleModelsDropdownData = [];
      state.vehicleCompanyBranchesDropdownData = [];
      state.vehicleVendorsDropdownData = [];
      state.vehicleThirdPartyOwnersDropdownData = [];
      state.vehicleDropdownLoadingCount = 0;
      state.vehicleDropdownError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVehicleTypesDropdown.fulfilled, (state, action) => {
        state.vehicleTypesDropdownData = extractPayloadData(action.payload);
      })
      .addCase(fetchVehicleConstructionTypesDropdown.fulfilled, (state, action) => {
        state.vehicleConstructionTypesDropdownData = extractPayloadData(action.payload);
      })
      .addCase(fetchVehicleTransmissionTypesDropdown.fulfilled, (state, action) => {
        state.vehicleTransmissionTypesDropdownData = extractPayloadData(action.payload);
      })
      .addCase(fetchVehicleMakesDropdown.fulfilled, (state, action) => {
        state.vehicleMakesDropdownData = extractPayloadData(action.payload);
      })
      .addCase(fetchVehicleModelsDropdown.fulfilled, (state, action) => {
        state.vehicleModelsDropdownData = extractPayloadData(action.payload);
      })
      .addCase(fetchVehicleCompanyBranchesDropdown.fulfilled, (state, action) => {
        state.vehicleCompanyBranchesDropdownData = extractPayloadData(action.payload);
      })
      .addCase(fetchVehicleVendorsDropdown.fulfilled, (state, action) => {
        state.vehicleVendorsDropdownData = extractPayloadData(action.payload);
      })
      .addCase(fetchVehicleThirdPartyOwnersDropdown.fulfilled, (state, action) => {
        state.vehicleThirdPartyOwnersDropdownData = extractPayloadData(action.payload);
      })
      .addMatcher(
        isAnyOf(
          fetchVehicleTypesDropdown.pending,
          fetchVehicleConstructionTypesDropdown.pending,
          fetchVehicleTransmissionTypesDropdown.pending,
          fetchVehicleMakesDropdown.pending,
          fetchVehicleModelsDropdown.pending,
          fetchVehicleCompanyBranchesDropdown.pending,
          fetchVehicleVendorsDropdown.pending,
          fetchVehicleThirdPartyOwnersDropdown.pending
        ),
        (state) => {
          state.vehicleDropdownLoadingCount += 1;
          state.vehicleDropdownError = null;
        }
      )
      .addMatcher(
        isAnyOf(
          fetchVehicleTypesDropdown.fulfilled,
          fetchVehicleConstructionTypesDropdown.fulfilled,
          fetchVehicleTransmissionTypesDropdown.fulfilled,
          fetchVehicleMakesDropdown.fulfilled,
          fetchVehicleModelsDropdown.fulfilled,
          fetchVehicleCompanyBranchesDropdown.fulfilled,
          fetchVehicleVendorsDropdown.fulfilled,
          fetchVehicleThirdPartyOwnersDropdown.fulfilled
        ),
        (state) => {
          state.vehicleDropdownLoadingCount = Math.max(
            0,
            state.vehicleDropdownLoadingCount - 1
          );
          state.vehicleDropdownError = null;
        }
      )
      .addMatcher(
        isAnyOf(
          fetchVehicleTypesDropdown.rejected,
          fetchVehicleConstructionTypesDropdown.rejected,
          fetchVehicleTransmissionTypesDropdown.rejected,
          fetchVehicleMakesDropdown.rejected,
          fetchVehicleModelsDropdown.rejected,
          fetchVehicleCompanyBranchesDropdown.rejected,
          fetchVehicleVendorsDropdown.rejected,
          fetchVehicleThirdPartyOwnersDropdown.rejected
        ),
        (state, action) => {
          state.vehicleDropdownLoadingCount = Math.max(
            0,
            state.vehicleDropdownLoadingCount - 1
          );
          state.vehicleDropdownError = action.payload;
        }
      );
  },
});

export const { clearVehicleDropdownState } = vehicleDropdownSlice.actions;

export default vehicleDropdownSlice.reducer;
